import React, { useState, useEffect, useCallback } from 'react';
import { Printer, RefreshCw, TestTube, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import printNodeService from '../../services/printNodeService';
import type { Printer as PrinterType, PrintersByComputer } from '../../types/printNode';

interface PrinterSelectProps {
  /** Current value - PrintNode printer ID or name as string */
  value?: string | number | null;
  /** Change handler */
  onChange: (value: string | null) => void;
  /** Which printer field to use as the select value. 'id' = PrintNode numeric ID (default), 'name' = printer name string */
  valueField?: 'id' | 'name';
  /** Label for the select */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Show test print button */
  showTestButton?: boolean;
  /** Additional class names */
  className?: string;
  /** Size variant */
  size?: 'sm' | 'md';
  /** Description/help text */
  description?: string;
}

interface PrinterSelectState {
  loading: boolean;
  printerGroups: PrintersByComputer[];
  error: string | null;
  testPrinting: boolean;
  testResult: 'success' | 'error' | null;
}

export const PrinterSelect: React.FC<PrinterSelectProps> = ({
  value,
  onChange,
  valueField = 'id',
  label,
  placeholder = '-- Velg skriver --',
  disabled = false,
  showTestButton = true,
  className = '',
  size = 'sm',
  description,
}) => {
  const { user } = useAuth();
  const [state, setState] = useState<PrinterSelectState>({
    loading: false,
    printerGroups: [],
    error: null,
    testPrinting: false,
    testResult: null,
  });

  const loadPrinters = useCallback(async () => {
    if (!user?.companyNo) return;

    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const groups = await printNodeService.getPrintersGroupedByComputer(user.companyNo);
      setState(prev => ({ ...prev, printerGroups: groups, loading: false }));
    } catch (err) {
      console.error('Failed to load printers:', err);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Kunne ikke laste skrivere. Sjekk PrintNode-tilkobling.',
      }));
    }
  }, [user?.companyNo]);

  useEffect(() => {
    loadPrinters();
  }, [loadPrinters]);

  // Clear test result after 3 seconds
  useEffect(() => {
    if (state.testResult) {
      const timer = setTimeout(() => {
        setState(prev => ({ ...prev, testResult: null }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [state.testResult]);

  const handleTestPrint = async () => {
    if (!user?.companyNo || !value) return;

    let printerId: number;
    let printerName: string | undefined;

    if (valueField === 'name') {
      // Value is a printer name - look up the ID. Samme navn kan finnes på
      // flere computere (f.eks. "Microsoft Print to PDF" på offline LBO og
      // online TJJ-LENOVO). Samle alle kandidater og foretrekk online.
      const valueStr = String(value);
      const matches = state.printerGroups.flatMap(g =>
        g.printers
          .filter(p => p.name === valueStr)
          .map(p => ({ printer: p, computer: g.computer }))
      );

      const isOnline = (s?: string) =>
        typeof s === 'string' && s.toLowerCase() === 'online';

      const preferred =
        matches.find(m => isOnline(m.printer.state) && isOnline(m.computer?.state))
        ?? matches.find(m => isOnline(m.computer?.state))
        ?? matches[0];

      if (preferred) {
        printerId = preferred.printer.id;
        printerName = preferred.printer.name;
        if (matches.length > 1) {
          console.info(
            `[PrinterSelect] '${valueStr}' har ${matches.length} treff i PrintNode; valgte id=${preferred.printer.id} på ${preferred.computer?.name} (${preferred.computer?.state ?? 'ukjent'})`
          );
        }
      }
      if (!printerId!) {
        setState(prev => ({ ...prev, error: 'Skriver ikke funnet i PrintNode' }));
        return;
      }
    } else {
      printerId = typeof value === 'string' ? parseInt(value, 10) : value;
      if (isNaN(printerId) || printerId <= 0) {
        setState(prev => ({ ...prev, error: 'Ugyldig skriver-ID' }));
        return;
      }
      // Find printer name for display
      for (const group of state.printerGroups) {
        const printer = group.printers.find(p => p.id === printerId);
        if (printer) {
          printerName = printer.name;
          break;
        }
      }
    }

    setState(prev => ({ ...prev, testPrinting: true, testResult: null, error: null }));
    try {
      await printNodeService.printTestPage(user.companyNo, printerId, printerName);
      setState(prev => ({ ...prev, testPrinting: false, testResult: 'success' }));
    } catch (err) {
      console.error('Test print failed:', err);
      setState(prev => ({
        ...prev,
        testPrinting: false,
        testResult: 'error',
        error: 'Testutskrift feilet',
      }));
    }
  };

  const hasValue = value !== null && value !== undefined && value !== '';
  const allPrinters: PrinterType[] = state.printerGroups.flatMap(g => g.printers);

  // Size-based styles
  const selectClasses = size === 'sm'
    ? 'px-2 py-1 text-sm'
    : 'px-3 py-2 text-base';

  const buttonClasses = size === 'sm'
    ? 'px-2 py-1 text-xs'
    : 'px-3 py-2 text-sm';

  return (
    <div className={`${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="flex items-center gap-2">
        {/* Printer icon */}
        <Printer className={`${size === 'sm' ? 'w-4 h-4' : 'w-5 h-5'} text-gray-400 flex-shrink-0`} />

        {/* Select dropdown */}
        <div className="relative flex-1">
          <select
            value={value?.toString() ?? ''}
            onChange={(e) => onChange(e.target.value || null)}
            disabled={disabled || state.loading}
            className={`w-full ${selectClasses} border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed`}
          >
            <option value="">{state.loading ? 'Laster...' : placeholder}</option>
            {state.printerGroups.map(group => (
              <optgroup key={group.computer.id} label={`${group.computer.name}`}>
                {group.printers.map(printer => (
                  <option key={printer.id} value={valueField === 'name' ? printer.name : printer.id}>
                    {printer.name} {printer.state !== 'online' ? `(${printer.state})` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
            {/* Fallback if no groups but we have a value - might be a manually entered ID */}
            {state.printerGroups.length === 0 && hasValue && (
              <option value={value?.toString()}>{value} (ikke funnet)</option>
            )}
          </select>
        </div>

        {/* Refresh button */}
        <button
          type="button"
          onClick={loadPrinters}
          disabled={state.loading || disabled}
          className={`${buttonClasses} text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors disabled:opacity-50`}
          title="Oppdater skriverliste"
        >
          <RefreshCw className={`${size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} ${state.loading ? 'animate-spin' : ''}`} />
        </button>

        {/* Test print button */}
        {showTestButton && (
          <button
            type="button"
            onClick={handleTestPrint}
            disabled={!hasValue || state.testPrinting || disabled}
            className={`${buttonClasses} flex items-center gap-1 rounded transition-colors disabled:opacity-50 ${
              state.testResult === 'success'
                ? 'bg-green-100 text-green-700 border border-green-300'
                : state.testResult === 'error'
                ? 'bg-red-100 text-red-700 border border-red-300'
                : 'bg-blue-50 text-blue-700 border border-blue-300 hover:bg-blue-100'
            }`}
            title="Send testutskrift"
          >
            {state.testPrinting ? (
              <Loader2 className={`${size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} animate-spin`} />
            ) : state.testResult === 'success' ? (
              <Check className={`${size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
            ) : state.testResult === 'error' ? (
              <AlertCircle className={`${size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
            ) : (
              <TestTube className={`${size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
            )}
            <span className="hidden sm:inline">Test</span>
          </button>
        )}
      </div>

      {/* Description */}
      {description && (
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      )}

      {/* Error message */}
      {state.error && (
        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {state.error}
        </p>
      )}

      {/* Success message */}
      {state.testResult === 'success' && (
        <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
          <Check className="w-3 h-3" />
          Testutskrift sendt
        </p>
      )}
    </div>
  );
};

export default PrinterSelect;
